import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import {
	CUSTOM_ATTRIBUTE_DECIMALS_MESSAGE,
	CUSTOM_ATTRIBUTE_MAX_DECIMAL_PLACES,
	exceedsCustomAttributeDecimals,
} from "$root/functional-areas/customAttributes/decimalCap";
import { createRefusedDecimalsNotice } from "$root/functional-areas/customAttributes/refusedDecimalsNotice";
import { marketViewRepository } from "$root/functional-areas/market-view/repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { useMappedQueries, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { sphereColumnsDef } from "$root/ui-lib/table/column/platform-columns-definition";
import {
	type ReferenceEntityValueDto,
	type ConceptDefinitionDTODataTypeEnum,
	ComparisonOperatorDTOKeywordEnum,
	type MandatoryItemData,
	type ConceptDefinitionDTO,
} from "@eu.mdotm.api/smart-workspaces";
import type { CustomAttributeValueDto, OptionModelDto, ReferenceUniverseListEntry } from "@eu.mdotm.api/sphere";
import { DatePickerInput, NumberInput, TextInput, Select, type SelectOption } from "@mdotm/shadcn/mdotm/components";
import { unreachable } from "@mdotm/utils/runtime";
import { useQuery } from "@tanstack/react-query";
import type { ColDef, IErrorValidationParams } from "ag-grid-community";
import { t } from "i18next";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ParameterizedConceptDTOWithMandatoryUuid } from "./ctx/context";
import { workspaceRepository } from "./repository";
import { type ComparisonChoice, isPresenceComparison } from "./comparison-operator";
import { Comparator, type OnChangeComparatorParams } from "./WorkspaceComparator";
import {
	type DataProviderResult,
	type WorkspaceDataCast,
	workspaceOptionsProviderCast,
} from "./WorkspaceDataDefinitionCast";

/**
 * # One spec per concept `dataType`
 *
 * Everything the app does differently *because of* a concept's `dataType` lives in
 * {@link workspaceDataTypes}: one row per data type, one field per question, closed with
 * `satisfies { [K in ConceptDefinitionDTODataTypeEnum]: … }` so a new data type cannot be
 * half-implemented — the compiler names the row, and the row names every question that row has to
 * answer.
 *
 * It replaces nine `switch`es and one 240-line component-per-type `if`-chain that each answered one of
 * these questions over the same enum, in five files. They were not independent: a cell's renderer, its
 * editor and the DTO its edit is written back as all describe one cell, and a filter's value control has
 * to accept what the comparison operators will be applied to. Split across five files they agreed only
 * by inspection, and four of them did not — see the notes on `SHARING_INFO`, on `CUSTOM_ATTRIBUTE_TAG`'s
 * qualifier, and on the three `CUSTOM_ATTRIBUTE_*` rows' `condition`.
 *
 * The *shape* of a data type's value — what an option is, what a cell is, and how the cell is
 * wrapped — is the one thing that is not here: it is the table in `WorkspaceDataDefinitionCast.ts`,
 * which has to stay a leaf of the module graph because `ui-lib`'s column definitions read it and this
 * file imports those. Two tables, same enum, same guard, and the split is the import graph's fault
 * rather than a judgement about what belongs together.
 *
 * Every field is *required and nullable* rather than optional, deliberately: `satisfies` does not
 * enforce optional properties, so an optional field would let a new data type answer four questions out
 * of five and still compile, which is the failure this table exists to prevent. `null` means "this data
 * type genuinely has no answer" and every `null` below says why.
 */
type WorkspaceDataTypeSpec<K extends ConceptDefinitionDTODataTypeEnum> = {
	/**
	 * How a value written into a grid cell becomes a `CustomAttributeValueDto` field, or `null` when the
	 * type is never editable in the grid.
	 *
	 * `attributeUuid` is the caller's to fill in, so a row only has to name the field it writes.
	 */
	customAttributeValue: ((newValue: unknown) => Omit<CustomAttributeValueDto, "attributeUuid">) | null;
	/** How a cell of this type renders, edits and sorts. */
	colDef(ctx: ConceptColDefContext): ColDef<Row>;
	/**
	 * How `/concepts/{conceptId}/options` items become the options of a value picker, or `null` when the
	 * type has no option list to pick from.
	 */
	parameterOptions: ParameterOptionsSpec<K> | null;
	/**
	 * Everything the filter chain's condition step needs for this type, or `null` when no filter can be
	 * built on it.
	 */
	condition: ConceptConditionSpec<K> | null;
};

/**
 * What a filter condition on this data type is made of.
 *
 * The chain used to answer these by hand, in ten `if`s that each named a `conditionType` and then repeated
 * the same props around it — which is how three of them came to name the *wrong* type. They are all
 * questions about the `dataType`, so they are answered here, beside the control that consumes them.
 */
type ConceptConditionSpec<K extends ConceptDefinitionDTODataTypeEnum> = {
	/** The control the value is taken in. */
	value(props: ConditionValueProps<K>): ReactNode;
	/** Whether the `in` operator is offered as a multi-select. */
	multiSelectOnIn: boolean;
	/** Whether the control fetches its own options and so has to be told which concept it is for. */
	needsConceptId: boolean;
	/** Whether the concept's unit of measurement is shown beside the value input. */
	showsUnit: boolean;
	/**
	 * Whether switching operator keeps the value entered under the previous one.
	 *
	 * `false` for the two types whose value is an identity rather than a quantity — an entity and an
	 * enumerated option — where "greater than the value you picked" is not a weakening of "equal to it".
	 * The remaining rows keep their value, and the split is preserved as it was found rather than
	 * reconciled.
	 */
	keepsValueOnOperatorChange: boolean;
	/**
	 * Which concepts of this type a condition can be built on, or `null` for all of them. Only
	 * `SPHERE_ENTITY` narrows: an entity condition is a pick from a published list of entities, and only
	 * the concepts in {@link sphereEntityConceptIds} have one.
	 */
	availableFor: ReadonlyArray<string> | null;
};

// --- The grid row ---

export type Row = { [key: string]: DataProviderResult<any> } & Required<MandatoryItemData>;
export type WorkspaceRow<D extends ConceptDefinitionDTODataTypeEnum> = Record<
	string,
	DataProviderResult<WorkspaceDataCast.DataProvider<D>>
> &
	Row;

/**
 * Reads one decoded cell out of a row.
 *
 * `dataType` is passed for its type alone — the row is a bag of `DataProviderResult`s keyed by
 * parameterized-concept uuid and nothing on it records which type it holds, so this is where the
 * caller's static knowledge is applied to the dynamic bag.
 */
export function getRowValue<T extends ConceptDefinitionDTODataTypeEnum>(
	row: Row | undefined,
	conceptUuid: string,
	_dataType: T,
): DataProviderResult<WorkspaceDataCast.DataProvider<T>> | undefined {
	const cell = row?.[conceptUuid];
	if (!cell || typeof cell !== "object") {
		return undefined;
	}
	return cell as DataProviderResult<WorkspaceDataCast.DataProvider<T>>;
}

export type TagOptionsMap = Record<string, { options: Array<Required<OptionModelDto>>; isLoading: boolean }>;

/** The stored ag-grid layout for a column, minus the sort keys the store owns. */
export type ColSettings = {
	hide?: boolean | null;
	width?: number;
	sort?: "asc" | "desc" | null;
	sortIndex?: number | null;
	pivot?: boolean | null;
	pivotIndex?: number | null;
	pinned?: "left" | "right" | null | false | true;
	rowGroup?: boolean | null;
	rowGroupIndex?: number | null;
} | null;

export type ConceptColDefContext = {
	concept: ConceptDefinitionDTO;
	parametrizedConcept: ParameterizedConceptDTOWithMandatoryUuid;
	/** The colDef every type shares — header, editability, menu, error highlighting — already assembled. */
	options: ColDef<Row>;
	colSettings: ColSettings;
	tagOptionsMap: TagOptionsMap;
	workspaceUuid?: string;
};

// --- Value pickers ---

/** The data types whose values are picked from a list the backend publishes. */
export type ConceptParameterDataType = Extract<
	ConceptDefinitionDTODataTypeEnum,
	"ENUM" | "STATUS" | "CUSTOM_ATTRIBUTE_TAG"
>;

/**
 * What a value picker for `K` carries.
 *
 * `CUSTOM_ATTRIBUTE_TAG` is the one type whose picker does not carry what the options endpoint publishes:
 * an option is a tag *definition* (a name and a colour) and what a filter or a cell stores is the name.
 */
type ParameterValue<K extends ConceptDefinitionDTODataTypeEnum> = K extends "CUSTOM_ATTRIBUTE_TAG"
	? string
	: WorkspaceDataCast.OptionsProvider<K>;

type ParameterOptionsSpec<K extends ConceptDefinitionDTODataTypeEnum> = {
	/** Kept per type because it always has been; see the note on `CUSTOM_ATTRIBUTE_TAG`. */
	qualifier: string;
	options(raw: Array<object>, translate: typeof t): Array<SelectOption<ParameterValue<K>>>;
};

/**
 * How an enum value is presented, in the one place that says so.
 *
 * Portfolio actions and workspace enums are two i18n namespaces over one value space, and a value that is
 * in neither is shown verbatim. The grid's `ENUM` column and the `ENUM` value picker are the two callers,
 * and they used to restate the merge separately.
 */
export function workspaceEnumLabel(value: string, translate: typeof t): string {
	const portfolioActions: Record<string, string> = translate("PORTFOLIO_ACTION_STATUS", { returnObjects: true });
	const workspaceEnums: Record<string, string> = translate("workspace.enums", { returnObjects: true });
	return { ...portfolioActions, ...workspaceEnums }[value] ?? value;
}

// --- Filter conditions ---

/**
 * The data types a filter condition can be built on — the same set as the rows of
 * {@link workspaceDataTypes} that answer `condition`, which the `satisfies` on that table enforces.
 */
export type SelectConceptConditionComparisonType = Extract<
	ConceptDefinitionDTODataTypeEnum,
	| "STRING"
	| "NUMERIC"
	| "DATE"
	| "DATETIME"
	| "ENUM"
	| "STATUS"
	| "BOOLEAN"
	| "SPHERE_ENTITY"
	| "SHARING_INFO"
	| "CUSTOM_ATTRIBUTE_TAG"
	| "CUSTOM_ATTRIBUTE_DATE"
	| "CUSTOM_ATTRIBUTE_NUMBER"
	| "CUSTOM_ATTRIBUTE_TEXT"
>;

type ConceptId<T extends SelectConceptConditionComparisonType> = T extends
	| "ENUM"
	| "STATUS"
	| "SPHERE_ENTITY"
	| "CUSTOM_ATTRIBUTE_TAG"
	? { conceptId: string }
	: { conceptId?: undefined };

type ValueHandlers<T extends SelectConceptConditionComparisonType> = T extends "ENUM" | "STATUS"
	?
			| {
					initialComparisonValue?: [
						WorkspaceDataCast.OptionsProvider<T> | null,
						WorkspaceDataCast.OptionsProvider<T> | null,
					]; //fix me relate the initial val based on the comparator value
					onChangeConceptComparisonValue(
						newValue: OnChangeComparatorParams<WorkspaceDataCast.OptionsProvider<T>>,
					): void;
					multi?: false;
			  }
			| {
					initialComparisonValue?: [
						WorkspaceDataCast.OptionsProvider<T>[] | null,
						WorkspaceDataCast.OptionsProvider<T>[] | null,
					]; //fix me relate the initial val based on the comparator value
					onChangeConceptComparisonValue(
						newValue: OnChangeComparatorParams<WorkspaceDataCast.OptionsProvider<T>[]>,
					): void;
					multi: true;
			  }
	: T extends "SPHERE_ENTITY" | "CUSTOM_ATTRIBUTE_TAG"
		?
				| {
						initialComparisonValue?: [string | null, string | null];
						onChangeConceptComparisonValue(newValue: OnChangeComparatorParams<string>): void;
						multi?: false;
				  }
				| {
						initialComparisonValue?: [string[] | null, string[] | null];
						onChangeConceptComparisonValue(newValue: OnChangeComparatorParams<string[]>): void;
						multi: true;
				  }
		: T extends "SHARING_INFO" // forced cast but actually user should be able to select the user list he can share with
			? {
					initialComparisonValue?: [boolean | null, boolean | null];
					onChangeConceptComparisonValue(newValue: OnChangeComparatorParams<boolean>): void;
					multi?: undefined;
				}
			: {
					initialComparisonValue?: [
						WorkspaceDataCast.OptionsProvider<T> | null,
						WorkspaceDataCast.OptionsProvider<T> | null,
					]; //fix me relate the initial val based on the comparator value
					onChangeConceptComparisonValue(
						newValue: OnChangeComparatorParams<WorkspaceDataCast.OptionsProvider<T>>,
					): void;
					multi?: undefined;
				};

export type SelectConceptConditionProps = {
	[ComparisonType in SelectConceptConditionComparisonType]: {
		allowedComparison: ComparisonOperatorDTOKeywordEnum[];
		comparisonMode: ComparisonOperatorDTOKeywordEnum | null;
		/**
		 * Which of the operator's offers the condition is on — the keyword for all but `exist`, which offers
		 * two. Carried beside `comparisonMode` because the two `exist` entries share a keyword and differ only
		 * in the argument they stand for; see `comparison-operator.ts`.
		 */
		comparisonChoice?: ComparisonChoice | null;
		onChangeComparisonMode(newChoice: ComparisonChoice | null): void;
		conditionType: ComparisonType;
		comparisonUnit?: string;
		disabled?: boolean;
	} & ConceptId<ComparisonType> &
		ValueHandlers<ComparisonType>;
}[SelectConceptConditionComparisonType];

/**
 * A condition's props once {@link SelectConceptConditionValue} has established there is an operator to
 * take a value for — every control needs one, and none of them has to re-check.
 */
type ConditionValueProps<K extends ConceptDefinitionDTODataTypeEnum> = Extract<
	SelectConceptConditionProps,
	{ conditionType: K }
> & { comparisonMode: ComparisonOperatorDTOKeywordEnum };

/**
 * The concepts an entity condition can be built on.
 *
 * The list {@link SphereEntityConditionValue}'s `switch` enumerates, named so the chain can ask the
 * question without restating it: an entity condition offers whatever the concept's own endpoint
 * publishes, and a concept with no endpoint has nothing to offer, so no condition step is rendered for
 * one at all. The chain used to keep its own copy of this list — as a map to the entity *types*, a prop
 * nothing has ever read.
 */
const sphereEntityConceptIds: ReadonlyArray<string> = [
	"systemId: name",
	"systemId: reference",
	"systemId: benchmark",
	"systemId: universe",
	"systemId: marketView",
];

// --- The spec ---

export const workspaceDataTypes = {
	BOOLEAN: {
		// Written as text because a boolean custom attribute is stored as one; nothing creates such an
		// attribute today, so this is reachable only through a hand-crafted concept.
		customAttributeValue: (newValue) => ({ text: String(newValue) }),
		colDef: (ctx) =>
			sphereColumnsDef.string<WorkspaceRow<"BOOLEAN">>(`${ctx.parametrizedConcept.uuid}.value`, {
				...ctx.options,
				valueGetter(params) {
					const cell = getRowValue(params.data, params.column.getColId(), "BOOLEAN");
					return cell?.value != null ? String(cell.value) : "";
				},
				...ctx.colSettings,
			}),
		// A boolean is not enumerated by the backend: the filter's two options are literals.
		parameterOptions: null,
		condition: {
			value: (props) => <YesNoConditionValue {...props} qualifier="SelectConceptConditionValue/BOOLEAN" />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: false,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	STRING: {
		customAttributeValue: (newValue) => ({ text: newValue as string }),
		colDef: (ctx) => textColDef(ctx, "STRING"),
		parameterOptions: null,
		condition: {
			value: (props) => <TextConditionValue {...props} />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: false,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	NUMERIC: {
		customAttributeValue: (newValue) => ({ numberValue: newValue as number }),
		colDef: (ctx) => numberColDef(ctx, "NUMERIC"),
		parameterOptions: null,
		condition: {
			value: (props) => <NumberConditionValue {...props} />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: true,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	DATE: {
		customAttributeValue: (newValue) => ({ dateValue: newValue as string }),
		colDef: (ctx) => dateColDef(ctx, "DATE"),
		parameterOptions: null,
		condition: {
			value: (props) => <DateConditionValue {...props} />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: false,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	TIME: {
		customAttributeValue: (newValue) => ({ dateValue: newValue as string }),
		colDef: (ctx) => dateColDef(ctx, "TIME"),
		parameterOptions: null,
		// `TIME` is not one of the types a filter condition is modelled for, so no chain ever asks.
		condition: null,
	},
	DATETIME: {
		customAttributeValue: (newValue) => ({ dateValue: newValue as string }),
		colDef: (ctx) => dateColDef(ctx, "DATETIME"),
		parameterOptions: null,
		condition: {
			value: (props) => <DateConditionValue {...props} />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: false,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	ENUM: {
		customAttributeValue: (newValue) => ({ text: newValue as string }),
		colDef: (ctx) =>
			sphereColumnsDef.string<WorkspaceRow<"ENUM">>(`${ctx.parametrizedConcept.uuid}.value`, {
				...ctx.options,
				valueGetter(params) {
					const cell = getRowValue(params.data, params.column.getColId(), "ENUM");
					return cell?.value ? workspaceEnumLabel(cell.value, t) : "";
				},
				...ctx.colSettings,
			}),
		parameterOptions: {
			qualifier: "SelectConceptParameter/ENUM",
			options: (raw, translate) =>
				raw.map((item) => {
					const value = workspaceOptionsProviderCast("ENUM")(item);
					return { value, label: workspaceEnumLabel(value, translate) };
				}),
		},
		condition: {
			value: (props) => <PickedConditionValue {...props} />,
			multiSelectOnIn: true,
			needsConceptId: true,
			showsUnit: false,
			keepsValueOnOperatorChange: false,
			availableFor: null,
		},
	},
	STATUS: {
		// The item status is computed by the backend, so there is nothing to write back.
		customAttributeValue: null,
		colDef: (ctx) =>
			sphereColumnsDef.workspaceStatus<WorkspaceRow<"STATUS">>(`${ctx.parametrizedConcept.uuid}.value`, {
				...ctx.options,
				valueGetter(params) {
					return getRowValue(params.data, params.column.getColId(), "STATUS")?.value;
				},
				...ctx.colSettings,
			}),
		parameterOptions: {
			qualifier: "SelectConceptParameter/STATUS",
			options: (raw, translate) =>
				raw.map((item) => {
					const value = workspaceOptionsProviderCast("STATUS")(item);
					return { value, label: translate(`workspace.entities.status.${value}`) };
				}),
		},
		condition: {
			value: (props) => <PickedConditionValue {...props} />,
			multiSelectOnIn: true,
			needsConceptId: true,
			showsUnit: false,
			// Unlike `ENUM`, whose row clears it. Preserved as found; see the note on the field.
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	SHARING_INFO: {
		// An ACL is edited through the share dialog, not through the cell.
		customAttributeValue: null,
		colDef: (ctx) => {
			// The sharing cell renders the ACL itself, so the generic value tooltip would cover it.
			const { tooltipComponent: _tooltipComponent, ...options } = ctx.options;
			return sphereColumnsDef.workspaceSharingInfo<WorkspaceRow<"SHARING_INFO">>(
				`${ctx.parametrizedConcept.uuid}.value`,
				{
					...options,
					valueGetter(params) {
						return getRowValue(params.data, params.column.getColId(), "SHARING_INFO")?.value;
					},
					...ctx.colSettings,
				},
			);
		},
		parameterOptions: null,
		// Identical to `BOOLEAN` bar the qualifier — "is it shared" is a yes/no question. It used to be
		// unreachable: the chain asked for a `BOOLEAN` condition on a `SHARING_INFO` concept, so this row
		// was never consulted and the control rendered under the `BOOLEAN` qualifier. The chain now passes
		// the concept's own `dataType`, which is what makes this row the one that answers.
		condition: {
			value: (props) => <YesNoConditionValue {...props} qualifier="SelectConceptConditionValue/SHARING_INFO" />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: false,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	TAGS: {
		customAttributeValue: (newValue) => ({ selectedTags: (newValue as string[]) ?? [] }),
		colDef: (ctx) => {
			const emptyTagOptions = { options: [] as Array<Required<OptionModelDto>>, isLoading: false };
			return sphereColumnsDef.workspaceCustomAttributeTags<WorkspaceRow<"TAGS">>(
				`${ctx.parametrizedConcept.uuid}.value`,
				{
					name: ctx.parametrizedConcept.label ?? "",
					uuid: ctx.parametrizedConcept.uuid,
					dataVisualization: "TAG_VIEW_WITH_COLOR",
				},
				{
					...ctx.options,
					cellRendererParams: emptyTagOptions,
					cellEditorParams: emptyTagOptions,
					tooltipComponentParams: emptyTagOptions,
					valueGetter(params) {
						const raw = getRowValue(params.data, params.column.getColId(), "TAGS")?.value;
						return raw ? [raw] : [];
					},
					...ctx.colSettings,
				},
			);
		},
		parameterOptions: null,
		// As `TIME`: not one of the types a filter condition is modelled for.
		condition: null,
	},
	SPHERE_ENTITY: {
		// The entity a workspace row *is*; renaming it is the entity's own concern.
		customAttributeValue: null,
		colDef: (ctx) => {
			// The cell renders the entity as a link with its own tooltip.
			const { tooltipComponent: _tooltipComponent, ...options } = ctx.options;
			return sphereColumnsDef.workspaceEntity<WorkspaceRow<"SPHERE_ENTITY">>(
				`${ctx.parametrizedConcept.uuid}.value`,
				{
					...options,
					valueGetter: ((params: any) => ({
						entity: getRowValue(params.data, params.column.getColId(), "SPHERE_ENTITY")?.value,
						richAcl: params.data?.richAcl,
					})) as any,
					lockVisible: ctx.parametrizedConcept.conceptId === "systemId: name",
					...ctx.colSettings,
				},
				ctx.workspaceUuid,
			);
		},
		// The entities are not one list: which endpoint publishes them depends on the concept, which is why
		// this type answers `condition` with a component of its own rather than with options.
		parameterOptions: null,
		condition: {
			value: (props) => <SphereEntityConditionValue {...props} />,
			multiSelectOnIn: true,
			needsConceptId: true,
			showsUnit: false,
			keepsValueOnOperatorChange: false,
			availableFor: sphereEntityConceptIds,
		},
	},
	CUSTOM_ATTRIBUTE_TAG: {
		customAttributeValue: (newValue) => ({ selectedTags: newValue as string[] }),
		colDef: (ctx) => {
			// The tag cell renders chips and answers its tooltip from `tooltipValueGetter`, so the raw-value
			// `tooltipField` the editable types get is dropped again here.
			const { tooltipField: _tooltipField, ...options } = ctx.options;
			const tagOptions = ctx.tagOptionsMap[ctx.concept.id!] ?? { options: [], isLoading: false };
			const tagValue = (params: { data?: Row }) =>
				(ctx.options.colId ? getRowValue(params.data, ctx.options.colId, "CUSTOM_ATTRIBUTE_TAG")?.value : []) ?? [];
			return sphereColumnsDef.workspaceCustomAttributeTags<WorkspaceRow<"CUSTOM_ATTRIBUTE_TAG">>(
				`${ctx.parametrizedConcept.uuid}.value`,
				{
					name: ctx.parametrizedConcept.label ?? "",
					uuid: ctx.parametrizedConcept.uuid,
					multi: ctx.concept.multiTag ?? false,
					dataVisualization: "TAG_VIEW_WITH_COLOR",
				},
				{
					...options,
					cellRendererParams: tagOptions,
					cellEditorParams: tagOptions,
					tooltipComponentParams: tagOptions,
					tooltipValueGetter: tagValue,
					valueGetter: tagValue,
					...ctx.colSettings,
				},
			);
		},
		parameterOptions: {
			// Says STATUS. A copy-paste that predates this table and that nothing reads — no POM, no spec and
			// no analytics select on it — so it is carried over verbatim rather than corrected in a commit
			// that is meant to change nothing.
			qualifier: "SelectConceptParameter/STATUS",
			options: (raw) =>
				raw.map((item) => {
					const tag = workspaceOptionsProviderCast("CUSTOM_ATTRIBUTE_TAG")(item);
					return { value: tag.value, label: tag.value };
				}),
		},
		condition: {
			value: (props) => <CustomAttributeTagConditionValue {...props} />,
			multiSelectOnIn: true,
			needsConceptId: true,
			showsUnit: false,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	CUSTOM_ATTRIBUTE_DATE: {
		customAttributeValue: (newValue) => ({ dateValue: newValue as string }),
		colDef: (ctx) => dateColDef(ctx, "CUSTOM_ATTRIBUTE_DATE"),
		parameterOptions: null,
		// A date is a date: the same control as `DATE`, referenced rather than copied. It used to be the
		// chain that knew this, by asking for a `DATE` condition on a `CUSTOM_ATTRIBUTE_DATE` concept — one
		// translation the chain had no business performing, and the reason this row could be left empty.
		condition: {
			value: (props) => <DateConditionValue {...props} />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: false,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	CUSTOM_ATTRIBUTE_NUMBER: {
		customAttributeValue: (newValue) => ({ numberValue: newValue as number }),
		colDef: (ctx) => customAttributeNumberColDef(ctx),
		parameterOptions: null,
		// As `CUSTOM_ATTRIBUTE_DATE`: the same control as `NUMERIC`, and the unit the chain always passed.
		condition: {
			value: (props) => <NumberConditionValue {...props} />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: true,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	CUSTOM_ATTRIBUTE_TEXT: {
		customAttributeValue: (newValue) => ({ text: newValue as string }),
		colDef: (ctx) => textColDef(ctx, "CUSTOM_ATTRIBUTE_TEXT"),
		parameterOptions: null,
		// As `CUSTOM_ATTRIBUTE_DATE`: the same control as `STRING`.
		condition: {
			value: (props) => <TextConditionValue {...props} />,
			multiSelectOnIn: false,
			needsConceptId: false,
			showsUnit: false,
			keepsValueOnOperatorChange: true,
			availableFor: null,
		},
	},
	TIME_SERIES: {
		// Chart-only: there is no scalar to write back.
		customAttributeValue: null,
		// Chart-only: the concept pickers never offer a TIME_SERIES concept as a column, so this is only
		// reachable through a hand-crafted workspace. An empty read-only column keeps the rest of the grid
		// alive, where throwing from a column factory would take the whole table down with it.
		colDef: (ctx) =>
			sphereColumnsDef.string<WorkspaceRow<"TIME_SERIES">>(`${ctx.parametrizedConcept.uuid}.value`, {
				...ctx.options,
				valueGetter: () => "",
				...ctx.colSettings,
			}),
		parameterOptions: null,
		condition: null,
	},
} satisfies {
	// The rows that answer `condition` are exactly the types {@link SelectConceptConditionProps} models a
	// condition for — the compiler names the row either way round, so neither list can grow without the
	// other.
	[K in ConceptDefinitionDTODataTypeEnum]: WorkspaceDataTypeSpec<K> &
		(K extends SelectConceptConditionComparisonType ? { condition: ConceptConditionSpec<K> } : { condition: null });
};

// --- Column definitions shared by more than one row ---

function textColDef(ctx: ConceptColDefContext, dataType: "STRING" | "CUSTOM_ATTRIBUTE_TEXT"): ColDef<Row> {
	return sphereColumnsDef.string<WorkspaceRow<typeof dataType>>(`${ctx.parametrizedConcept.uuid}.value`, {
		...ctx.options,
		valueGetter(params) {
			const cell = getRowValue(params.data, params.column.getColId(), dataType);
			return cell?.value ?? "";
		},
		...ctx.colSettings,
	});
}

function numberColDef(ctx: ConceptColDefContext, dataType: "NUMERIC" | "CUSTOM_ATTRIBUTE_NUMBER"): ColDef<Row> {
	return sphereColumnsDef.workspaceNumber<WorkspaceRow<typeof dataType>>(
		`${ctx.parametrizedConcept.uuid}`,
		{
			// A custom attribute shows the value as entered, up to 13 decimals.
			decimalPlaces:
				dataType === "CUSTOM_ATTRIBUTE_NUMBER"
					? { minDecimalPlaces: 0, maxDecimalPlaces: CUSTOM_ATTRIBUTE_MAX_DECIMAL_PLACES }
					: (ctx.concept.numericDecimalPlaces ?? 0),
			percentage: ctx.concept.unitOfMeasurement === "%",
			// A metric with no value is worth reporting; an unset custom attribute is just empty.
			hideMissingMessage: dataType === "CUSTOM_ATTRIBUTE_NUMBER",
		},
		{
			...ctx.options,
			...ctx.colSettings,
		},
	);
}

/**
 * A custom attribute's number column refuses a value past 13 decimals: the editor shows the message in its
 * tooltip and the grid reverts the edit, and paste and fill, which skip the editor, store nothing and say so
 * in a toast.
 */
function customAttributeNumberColDef(ctx: ConceptColDefContext): ColDef<Row> {
	const base = numberColDef(ctx, "CUSTOM_ATTRIBUTE_NUMBER");
	const noticeRefused = createRefusedDecimalsNotice();
	return {
		...base,
		cellEditorParams: {
			...base.cellEditorParams,
			getValidationErrors: ({ value }: IErrorValidationParams<Row, number>) =>
				value != null && exceedsCustomAttributeDecimals(value) ? [CUSTOM_ATTRIBUTE_DECIMALS_MESSAGE] : null,
		},
		valueSetter(params) {
			if (exceedsCustomAttributeDecimals(Number(params.newValue))) {
				noticeRefused();
				return false;
			}
			return typeof base.valueSetter === "function" && base.valueSetter(params);
		},
		// AG Grid's own Escape rebuilds an editor that fails validation and leaves it orphaned, open for good.
		suppressKeyboardEvent({ editing, event, api }) {
			if (!editing || event.type !== "keydown" || event.key !== "Escape") {
				return false;
			}
			api.stopEditing(true);
			return true;
		},
	};
}

/**
 * The date column, plus the two sync timestamps that are dates on the wire and a status in the grid.
 *
 * `metricsSyncDate` and `autosyncDate` render "how fresh is this row" rather than a formatted date, which
 * is a property of those two concepts and not of their `dataType` — hence a branch on `concept.id` inside
 * one row rather than a data type of their own.
 */
function dateColDef(
	ctx: ConceptColDefContext,
	dataType: "DATE" | "DATETIME" | "TIME" | "CUSTOM_ATTRIBUTE_DATE",
): ColDef<Row> {
	if (ctx.concept.id === "systemId: metricsSyncDate" || ctx.concept.id === "systemId: autosyncDate") {
		const { tooltipComponent: _tooltipComponent, ...options } = ctx.options;
		return sphereColumnsDef.workspaceSyncDate<WorkspaceRow<typeof dataType>>(
			`${ctx.parametrizedConcept.uuid}.value`,
			ctx.concept.id === "systemId: metricsSyncDate" ? "metrics-sync" : "investment-sync",
			{
				...options,
				minWidth: 50,
				valueGetter(params) {
					if (!params.colDef.colId) {
						return undefined;
					}
					return getRowValue(params.data, params.colDef.colId, dataType)?.value;
				},
				...ctx.colSettings,
			},
		);
	}

	return sphereColumnsDef.date<WorkspaceRow<typeof dataType>>(`${ctx.parametrizedConcept.uuid}.value`, {
		...ctx.options,
		valueGetter(params) {
			if (!params.colDef.colId) {
				return "";
			}
			return getRowValue(params.data, params.colDef.colId, dataType)?.value;
		},
		...ctx.colSettings,
	});
}

// --- Dispatchers ---

/**
 * The colDef for a concept's column.
 *
 * The generic `tooltipField` is the one thing decided here rather than per row: it shows the raw cell
 * value, which is right for the types that render their value as text and wrong for the four that render
 * something else — each of those drops it again.
 */
export function conceptColDef(dataType: ConceptDefinitionDTODataTypeEnum, ctx: ConceptColDefContext): ColDef<Row> {
	return workspaceDataTypes[dataType].colDef({
		...ctx,
		options: ctx.concept.editable ? { ...ctx.options, tooltipField: `${ctx.parametrizedConcept.uuid}` } : ctx.options,
	});
}

/**
 * The DTO an edited cell is written back as, or `undefined` when the type has nothing to write.
 *
 * Only the four `CUSTOM_ATTRIBUTE_*` types are reachable — the grid's cell-changed handler refuses
 * anything else before it gets here — but the rows that answer are the rows whose value the API can
 * carry, which is a fact about the DTO rather than about who happens to call.
 */
export function cellValueToCustomAttributeDto(
	attributeUuid: string,
	dataType: ConceptDefinitionDTODataTypeEnum,
	newValue: unknown,
): CustomAttributeValueDto | undefined {
	const write = workspaceDataTypes[dataType].customAttributeValue;
	return write ? { attributeUuid, ...write(newValue) } : undefined;
}

/** A picker over the values `/concepts/{conceptId}/options` publishes for a concept. */
export type SelectConceptParameterProps = {
	[K in ConceptParameterDataType]: {
		dataType: K;
		conceptId: string;
		disabled?: boolean;
	} & (
		| {
				value: ParameterValue<K>[];
				onChange(newValue: ParameterValue<K>[]): void;
				multi: true;
		  }
		| {
				value: ParameterValue<K> | null;
				onChange(newValue: ParameterValue<K> | null): void;
				multi?: false;
		  }
	);
}[ConceptParameterDataType];

export function SelectConceptParameter(props: SelectConceptParameterProps) {
	const { t: translate } = useTranslation();
	const spec = workspaceDataTypes[props.dataType].parameterOptions;
	return (
		<ReactQueryWrapperBase query={workspaceRepository.getConceptOptions.useQuery(props.conceptId)}>
			{(conceptOptions) => {
				if (!spec) {
					return unreachable();
				}
				// The one cast this dispatch needs: `props` and `spec` are keyed on the same `dataType`, but
				// TypeScript cannot relate two members of a union it has narrowed separately.
				const options = spec.options(conceptOptions, translate as typeof t) as Array<SelectOption<unknown>>;
				const shared = {
					options,
					disabled: props.disabled,
					triggerDataAttrs: { "data-qualifier": spec.qualifier },
				};
				return props.multi ? (
					<Select
						{...shared}
						multi
						value={Array.isArray(props.value) ? props.value : []}
						onChange={props.onChange as (newValue: unknown[]) => void}
					/>
				) : (
					<Select {...shared} value={props.value} onChange={props.onChange as (newValue: unknown) => void} />
				);
			}}
		</ReactQueryWrapperBase>
	);
}

/**
 * The control a filter condition takes its value in.
 *
 * No operator means nothing to take, and neither does a presence operator — its argument is which of its
 * two entries the user picked in the select above. That is what every type has in common and therefore the
 * only thing decided here.
 */
export function SelectConceptConditionValue(props: SelectConceptConditionProps) {
	if (!props.comparisonMode || isPresenceComparison(props.comparisonMode)) {
		return null;
	}

	const render = workspaceDataTypes[props.conditionType].condition?.value;
	// As in `SelectConceptParameter`: one cast for a correlated union.
	return render ? (render as (p: SelectConceptConditionProps) => ReactNode)(props) : null;
}

// --- Condition-value controls shared by more than one row ---

/** A free-text value: the whole of a `STRING` or `CUSTOM_ATTRIBUTE_TEXT` condition. */
function TextConditionValue(props: ConditionValueProps<"STRING"> | ConditionValueProps<"CUSTOM_ATTRIBUTE_TEXT">) {
	return (
		<Comparator<string>
			onChange={props.onChangeConceptComparisonValue}
			comparator={props.comparisonMode}
			initialValue={props.initialComparisonValue}
			component={(params) => (
				<TextInput
					value={(params.value as string) ?? ""}
					onChangeText={params.onChange}
					size="small"
					disabled={props.disabled}
				/>
			)}
		/>
	);
}

/** A number, with the concept's unit shown inside the input. */
function NumberConditionValue(props: ConditionValueProps<"NUMERIC"> | ConditionValueProps<"CUSTOM_ATTRIBUTE_NUMBER">) {
	return (
		<Comparator<number>
			onChange={props.onChangeConceptComparisonValue}
			comparator={props.comparisonMode}
			initialValue={props.initialComparisonValue}
			component={(params) => (
				<NumberInput
					value={params.value as number | null}
					onChange={params.onChange}
					size="small"
					rightContent={props.comparisonUnit}
					disabled={props.disabled}
				/>
			)}
		/>
	);
}

function YesNoConditionValue(
	props: (ConditionValueProps<"BOOLEAN"> | ConditionValueProps<"SHARING_INFO">) & { qualifier: string },
) {
	return (
		<Comparator<boolean>
			onChange={props.onChangeConceptComparisonValue}
			comparator={props.comparisonMode}
			initialValue={props.initialComparisonValue}
			component={(params) => (
				<Select
					triggerDataAttrs={{ "data-qualifier": props.qualifier }}
					options={[
						{ label: "Yes", value: true },
						{ label: "No", value: false },
					]}
					value={params.value}
					onChange={params.onChange}
				/>
			)}
		/>
	);
}

function DateConditionValue(
	props: ConditionValueProps<"DATE"> | ConditionValueProps<"DATETIME"> | ConditionValueProps<"CUSTOM_ATTRIBUTE_DATE">,
) {
	return (
		<Comparator<string>
			onChange={props.onChangeConceptComparisonValue}
			comparator={props.comparisonMode}
			initialValue={props.initialComparisonValue}
			component={(params) => (
				<DatePickerInput {...params} value={params.value as string} size="small" disabled={props.disabled} />
			)}
		/>
	);
}

/** The types whose condition value is one of the options the concept publishes. */
function PickedConditionValue(props: ConditionValueProps<"ENUM"> | ConditionValueProps<"STATUS">) {
	// `SelectConceptParameter`'s props relate `dataType` to `value`, and this control deliberately serves the
	// two types that pick their value the same way — so the pair has to be re-narrowed to hand it over. The
	// `value` casts below are the same seam seen from the other side, and predate this table.
	const dataType = props.conditionType as "ENUM";

	if (props.multi) {
		return (
			<Comparator<string[]>
				onChange={props.onChangeConceptComparisonValue}
				comparator={props.comparisonMode}
				initialValue={props.initialComparisonValue}
				component={(params) => (
					<SelectConceptParameter
						dataType={dataType}
						conceptId={props.conceptId}
						onChange={params.onChange}
						// TODO: typing is wrong
						value={(Array.isArray(params.value) ? params.value : []) as any}
						multi
					/>
				)}
			/>
		);
	}

	return (
		<Comparator<string>
			onChange={props.onChangeConceptComparisonValue}
			comparator={props.comparisonMode}
			initialValue={props.initialComparisonValue}
			component={(params) => (
				<SelectConceptParameter
					dataType={dataType}
					conceptId={props.conceptId}
					onChange={params.onChange}
					// TODO: typing is wrong
					value={params.value as any}
				/>
			)}
		/>
	);
}

/**
 * A tag condition: picked from the concept's tags, except under `contains`, which matches a substring of
 * the tag name and so takes free text.
 */
function CustomAttributeTagConditionValue(props: ConditionValueProps<"CUSTOM_ATTRIBUTE_TAG">) {
	if (props.multi) {
		return (
			<Comparator<string[]>
				onChange={props.onChangeConceptComparisonValue}
				comparator={props.comparisonMode}
				initialValue={props.initialComparisonValue}
				component={(params) => (
					<SelectConceptParameter
						dataType={props.conditionType}
						conceptId={props.conceptId}
						onChange={params.onChange}
						value={(Array.isArray(params.value) ? params.value : []) as any}
						multi
					/>
				)}
			/>
		);
	}

	return (
		<Comparator<string>
			onChange={props.onChangeConceptComparisonValue}
			comparator={props.comparisonMode}
			initialValue={props.initialComparisonValue}
			component={(params) =>
				props.comparisonMode === ComparisonOperatorDTOKeywordEnum.Contains ? (
					<TextInput
						value={(params.value as string) ?? ""}
						onChangeText={params.onChange}
						size="small"
						disabled={props.disabled}
					/>
				) : (
					<SelectConceptParameter
						dataType={props.conditionType}
						conceptId={props.conceptId}
						onChange={params.onChange}
						value={params.value as string | null}
					/>
				)
			}
		/>
	);
}

type SphereEntityFilterValue = { name: string; uuid: string };

/**
 * An entity condition.
 *
 * The one type whose options are not a single endpoint: investments, references and benchmarks come from
 * the workspace's own comparison-entities endpoint, universes and market views from their areas. The
 * picker carries entities and the predicate carries uuids, so the chosen entities are held here to be
 * shown while only their uuids are reported.
 */
function SphereEntityConditionValue(props: ConditionValueProps<"SPHERE_ENTITY">) {
	const [sphereEntities, setSphereEntities] = useState<Array<SphereEntityFilterValue>>([]);
	const entitiesQuery = useQuery({
		queryKey: [props.conceptId],
		queryFn: async () => {
			switch (props.conceptId) {
				case "systemId: name":
					return {
						kind: "investments" as const,
						list: await workspaceRepository.availableComparisonEntities.fetchQuery(props.conceptId),
					};
				case "systemId: reference":
				case "systemId: benchmark":
					return {
						kind: "references" as const,
						list: await workspaceRepository.availableComparisonEntities.fetchQuery(props.conceptId),
					};
				case "systemId: universe":
					return { kind: "universes" as const, list: await universeRepository.listBaseAndGroup.fetchQuery() };
				case "systemId: marketView":
					return { kind: "marketViews" as const, list: await marketViewRepository.list.fetchQuery() };
				default:
					return null;
			}
		},
	});
	const mappedOptionsQuery = useMappedQueries(
		([entities]) => {
			if (entities) {
				return entities.list.flatMap((entity) => {
					if (entity.uuid) {
						const baseEntity = { label: entity.name!, value: { name: entity.name!, uuid: entity.uuid } };
						switch (entities.kind) {
							case "references":
								if ((entity as ReferenceEntityValueDto).type !== "INVESTMENT") {
									return [{ ...baseEntity, group: (entity as ReferenceEntityValueDto).type }];
								} else {
									return [];
								}
							case "universes":
								return [
									{
										...baseEntity,
										group:
											(entity as ReferenceUniverseListEntry).universeType === "INSTRUMENTS_UNIVERSE"
												? "UNIVERSE"
												: "UNIVERSE WITH GROUPS",
									},
								];
							default:
								return [baseEntity];
						}
					} else {
						return [];
					}
				});
			}
		},
		[],
		[entitiesQuery],
	);

	useQueryReactor(mappedOptionsQuery, {
		onSuccess(entitiesOptions) {
			if (props.comparisonMode === "contains") {
				return;
			}

			if (props.multi) {
				const initialOptions = props.initialComparisonValue?.[0]?.flatMap((uuid) => {
					const matchedEntity = entitiesOptions.find((entity) => entity.value.uuid === uuid);
					return matchedEntity ? [matchedEntity.value] : [];
				});

				setSphereEntities(initialOptions ?? []);
				return;
			}

			const uuid = props.initialComparisonValue?.[0];
			const matchedEntity = entitiesOptions.find((entity) => entity.value.uuid === uuid)?.value;
			setSphereEntities(matchedEntity ? [matchedEntity] : []);
		},
	});

	return (
		<ReactQueryWrapperBase query={mappedOptionsQuery}>
			{(options) =>
				props.multi ? (
					<Comparator<string[]>
						onChange={(newValue) => {
							props.onChangeConceptComparisonValue(newValue);
							if (newValue.data.filter((x) => x).length === 0) {
								setSphereEntities([]);
							}
						}}
						comparator={props.comparisonMode}
						initialValue={props.initialComparisonValue}
						component={(params) => (
							<Select
								triggerDataAttrs={{ "data-qualifier": "SelectSphereEntityConceptConditionValue" }}
								enableSearch
								value={sphereEntities}
								onChange={(newEntities) => {
									const uuids = newEntities.flatMap((entity) => (entity.uuid ? [entity.uuid] : []));
									params.onChange(uuids);
									setSphereEntities(newEntities);
								}}
								options={options}
								multi
							/>
						)}
					/>
				) : props.comparisonMode === "contains" ? (
					<Comparator<string>
						onChange={(newValue) => {
							props.onChangeConceptComparisonValue(newValue);
							if (newValue.data.filter((x) => x).length === 0) {
								setSphereEntities([]);
							}
						}}
						comparator={props.comparisonMode}
						initialValue={props.initialComparisonValue}
						component={(params) => (
							<TextInput
								value={(params.value as string) ?? ""}
								onChangeText={params.onChange}
								size="small"
								disabled={props.disabled}
							/>
						)}
					/>
				) : (
					<Comparator<string>
						onChange={(newValue) => {
							props.onChangeConceptComparisonValue(newValue);
							if (newValue.data.filter((x) => x).length === 0) {
								setSphereEntities([]);
							}
						}}
						comparator={props.comparisonMode}
						initialValue={props.initialComparisonValue}
						component={(params) => (
							<Select
								triggerDataAttrs={{ "data-qualifier": "SelectSphereEntityConceptConditionValue" }}
								enableSearch
								value={sphereEntities.at(0) ?? null}
								onChange={(newEntities) => {
									params.onChange(newEntities?.uuid ?? null);
									setSphereEntities(newEntities?.uuid ? [newEntities] : []);
								}}
								options={options}
							/>
						)}
					/>
				)
			}
		</ReactQueryWrapperBase>
	);
}
